
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { getCardBrand, getCardBrandIcon, isCardExpired } from '@/utils/cardUtils';
import warningIcon from '@/assets/warning.svg';
import visaIcon from '@/assets/cards/visa.svg';
import mastercardIcon from '@/assets/cards/mastercard.svg';
import amexIcon from '@/assets/cards/amex.svg';
import dinersIcon from '@/assets/cards/diners.svg';
import jcbIcon from '@/assets/cards/jcb.svg';

const PaymentCard = ({ 
  card, 
  isDefault, 
  onSetDefault, 
  onDelete 
}) => {
  const { t } = useTranslation();
  
  const cardBrand = getCardBrand(card.number);
  const expired = isCardExpired(card.expiryDate);
  
  const getCardIcon = () => {
    switch (cardBrand) {
      case 'visa':
        return visaIcon;
      case 'mastercard':
        return mastercardIcon;
      case 'amex':
        return amexIcon;
      case 'diners':
        return dinersIcon;
      case 'jcb':
        return jcbIcon;
      default:
        return mastercardIcon; // Default fallback
    }
  };

  return (
    <div className="flex-1 p-4 relative bg-white rounded-md outline outline-1 outline-[#dddddd] inline-flex flex-col justify-start items-start gap-4 overflow-hidden min-h-[182px]">
      {/* Card Brand Icon */}
      <div className="w-10 h-10 relative overflow-hidden">
        <img src={getCardIcon()} alt={cardBrand} className="w-10 h-10" />
      </div>

      {/* Card Info */}
      <div className="self-stretch flex-1 flex flex-col justify-start items-start gap-2">
        <div className="self-stretch inline-flex justify-start items-center gap-4">
          <div className="justify-center text-[#111111] text-base font-medium leading-normal">
            {card.number}
          </div>
          <div className="justify-center text-[#111111] text-base font-normal leading-normal">
            {card.expiryDate}
          </div>
        </div>

        {/* Expiry Warning */}
        {expired && (
          <div className="inline-flex justify-start items-center gap-1">
            <img src={warningIcon} alt="warning" className="w-3.5 h-3.5" />
            <div className="justify-center text-[#ff2b00] text-xs font-normal leading-[18px]">
              {t('me.payment.cardExpired')}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="self-stretch inline-flex justify-start items-center gap-2.5">
        <Button
          onClick={() => onDelete(card)}
          variant="default"
          size="s"
          className="h-7 px-3.5 rounded-3xl outline outline-1 outline-offset-[-1px] outline-[#dddddd]"
        >
          <span className="text-center justify-center text-[#111111] text-xs font-normal leading-[18px]">
            {t('common.delete')}
          </span>
        </Button>

        <div className="flex-1 flex justify-end items-center gap-1">
          <Checkbox
            checked={isDefault}
            onCheckedChange={() => !isDefault && onSetDefault(card)}
            size={4}
          />
          <div className="justify-center text-[#666666] text-xs font-normal">
            {t('me.payment.setAsDefault')}
          </div>
        </div>
      </div>

      {/* Default Badge */}
      {isDefault && (
        <div className="h-5 min-w-16 px-1 py-px absolute right-4 top-4 bg-[#2a66ff]/10 rounded-sm inline-flex justify-center items-center">
          <div className="text-center justify-center text-[#2966ff] text-xs font-normal leading-[18px]">
            {t('me.payment.default')}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCard;
